/**
 * Screen Saver page — resolves an explicit siteId using existing Dashboard patterns.
 * Does not invent a global current-site context.
 * Internal route remains `/ambient`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getSites } from '../api/backendApi';
import { resolveDashboardWeatherSites } from '../components/weather/resolveDashboardWeatherSites';
import AmbientMode from '../components/ambient/AmbientMode';
import { ROUTES } from '../utils/constants';

function resolveReturnTo(stateReturnTo) {
  if (typeof stateReturnTo === 'string' && stateReturnTo.startsWith('/')) {
    return stateReturnTo;
  }
  return null;
}

export default function AmbientPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { userClaims, userData } = useAuth();
  const [sites, setSites] = useState(null);

  const preferredSiteId = userClaims?.siteId || userData?.siteId || null;
  const querySiteId = String(searchParams.get('siteId') || '').trim();
  const returnTo = resolveReturnTo(location.state?.returnTo);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSites();
        if (cancelled) return;
        if (resp?.success === false) {
          setSites([]);
          return;
        }
        const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        setSites(list);
      } catch {
        if (cancelled) return;
        setSites([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolved = useMemo(() => {
    if (!Array.isArray(sites)) return null;
    const base = resolveDashboardWeatherSites(sites, preferredSiteId);
    if (querySiteId && base.sites.some((s) => s.id === querySiteId)) {
      const match = base.sites.find((s) => s.id === querySiteId);
      return { siteId: match.id, siteName: match.name, sites: base.sites };
    }
    return base;
  }, [sites, preferredSiteId, querySiteId]);

  const handleExit = useCallback(() => {
    // Home button flow: always return to /home (do not rely on history.back).
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(ROUTES.HOME_LAUNCHER, { replace: true });
  }, [navigate, returnTo]);

  if (sites === null) {
    return (
      <Box
        data-testid="ambient-page-loading"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          bgcolor: '#071018',
          color: '#E2E8F0',
          zIndex: 1600,
        }}
      >
        <CircularProgress size={22} sx={{ color: 'rgba(148,197,220,0.9)' }} />
        <Typography>Preparing Screen Saver…</Typography>
      </Box>
    );
  }

  return (
    <AmbientMode
      siteId={resolved?.siteId || null}
      siteName={resolved?.siteName || null}
      onExit={handleExit}
    />
  );
}
